import type { Advice } from './project-advisor.ts';
import { advisorAddonPrice, catalog, normalizeModules } from './project-pricing.ts';
import { applyJourneyAdvice, initialJourney, installedPlatformIds, journeyModuleIds, platformIds, websiteExtras, websiteIds, type Journey } from './project-journey.ts';

export type AdvisorAnswers = { platforms: string[]; website: string };
export type AdvisorSelection = { areas: string[]; recommended: string[]; platforms: string[]; website: string; discounted: string[]; customQuote: boolean };

export function advisorNeeds(advice: Advice) {
  const ids = normalizeModules(advice.recommended);
  return {
    website: advice.areas.includes('website') || ids.some(id => websiteIds.some(value => value === id) || websiteExtras.some(value => value === id)),
    application: advice.areas.some(id => ['app', 'automation', 'ai', 'flowmate'].includes(id)) || ids.some(id => !websiteIds.some(value => value === id) && !websiteExtras.some(value => value === id)),
  };
}

export function initialAdvisorAnswers(advice: Advice): AdvisorAnswers {
  const platforms = advice.platforms.filter(id => platformIds.some(value => value === id));
  const installed = advice.recommended.filter(id => installedPlatformIds.some(value => value === id));
  return { platforms: [...new Set([...platforms, ...installed])], website: advice.website || advice.recommended.find(id => websiteIds.some(value => value === id)) || '' };
}

export function advisorReady(advice: Advice, answers: AdvisorAnswers) {
  const needs = advisorNeeds(advice);
  return (needs.website || needs.application) && (!needs.application || answers.platforms.some(id => platformIds.some(value => value === id))) && (!needs.website || websiteIds.some(id => id === answers.website));
}

export function advisorRequired(advice: Advice, answers: AdvisorAnswers) {
  const needs = advisorNeeds(advice);
  const platforms = answers.platforms.includes('all') ? [...installedPlatformIds] : answers.platforms.filter(id => installedPlatformIds.some(value => value === id));
  const required = normalizeModules([
    ...advice.recommended.filter(id => !websiteIds.some(value => value === id) && !installedPlatformIds.some(value => value === id)),
    ...(needs.website && answers.website ? [answers.website] : []),
    ...(needs.application ? ['flowmate', ...platforms] : []),
  ]);
  return journeyModuleIds(applyJourneyAdvice(initialJourney(), { areas: advice.areas, recommended: required, platforms: answers.platforms, website: answers.website }));
}

export function advisorExtras(advice: Advice, required: string[], alreadySelected: string[]) {
  const needs = advisorNeeds(advice);
  return [...new Set(advice.optional)].filter(id => {
    if (!advisorAddonPrice(id) || required.includes(id) || alreadySelected.includes(id) || (!needs.website && websiteExtras.some(value => value === id)) || (!needs.application && !websiteExtras.some(value => value === id)) || required.some(selected => catalog.find(item => item.id === selected)?.replaces === id)) return false;
    const expanded = journeyModuleIds(applyJourneyAdvice(initialJourney(), { areas: advice.areas, recommended: [...required, id] }));
    return expanded.every(value => value === id || required.includes(value) || alreadySelected.includes(value));
  });
}

export function advisorSelection(advice: Advice, answers: AdvisorAnswers, required: string[], optional: string[], eligibleExtras: string[]): AdvisorSelection {
  const selectedExtras = optional.filter(id => eligibleExtras.includes(id));
  const recommended = normalizeModules([...required, ...selectedExtras]);
  return { areas: advice.areas, recommended, platforms: answers.platforms, website: answers.website, discounted: selectedExtras.filter(id => recommended.includes(id) && advisorAddonPrice(id)), customQuote: advice.customQuote };
}

export function advisorPreview(selection: AdvisorSelection, current: Journey = initialJourney()) {
  return applyJourneyAdvice(current, selection);
}
